import React, {useEffect, useRef, useState} from 'react'
import {Box, Button, IconButton, InputAdornment, TextField, Typography} from '@mui/material'
import {openReceipt, OpenReceiptResult, searchTransfers, TransferOrderResult} from '../transferUtils'
import Alert from '@mui/material/Alert'
import CheckIcon from '@mui/icons-material/Check'
import {useAuth} from '../../auth/auth'
import {useLocation, useNavigate} from 'react-router-dom'
import {CachedProduct, getCachedProducts} from '../productCache'
import ReceiveDashboardTable from './ReceiveDashboardTable'
import {webEvents} from '../../common/WebEvents'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import {useDispatch} from 'react-redux'
import {createError} from '../../store/slices/error'
import {RouteSecurity} from '../../common/RouteSecurity'

function ReceiveDashboard() {
    const {getTruck, getPermissions} = useAuth()
    const truckName = getTruck()
    const [transferOrders, setTransferOrders] = useState<TransferOrderResult[]>([])
    const [resumeOrder, setResumeOrder] = useState(() => {
        const resumeId = window.sessionStorage.getItem('receiving')
        return resumeId ? JSON.parse(resumeId) : null
    })
    const [resumeOrderId, setResumeOrderId] = useState<string>('')
    const [selectedId, setSelectedId] = useState<string>('')
    const [receiveSubmitted, setReceiveSubmitted] = useState(false)
    const [products, setProducts] = useState<CachedProduct[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [highlightedItems, setHighlightedItems] = useState<string[]>([])
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    let inputRef = useRef<HTMLInputElement>(null)
    const routeSecurity = new RouteSecurity(getPermissions())
    const isAuthorizedToReceive = routeSecurity.isAuthorized('/inventory/receiving')


    const getModelNumber = (sku: string) => {
        const product = products.find(p => p.sku === sku)
        return product ? product.model : 'Unknown Model'
    }

    useEffect(() => {
        let receiveSuccessItem = window.sessionStorage.getItem('receiveSuccess')
        if (receiveSuccessItem === 'true') {
            setReceiveSubmitted(true)
            setResumeOrder(null)
            window.sessionStorage.setItem('receiveSuccess', 'false')
            return
        }
        const fetchProducts = async () => {
            const cachedProducts = await getCachedProducts()
            setProducts(Object.values(cachedProducts))
        }

        fetchProducts()
    }, [])

    useEffect(() => {
        const receivedSuccessItem = window.sessionStorage.getItem('receiveSuccess')
        if (receivedSuccessItem === 'true') {
            setReceiveSubmitted(true)
            setResumeOrder(null)
            window.sessionStorage.setItem('receiveSuccess', 'false')
            window.sessionStorage.removeItem('receiving')
            return
        }
        let isMounted = true

        searchTransfers({
            types: 'replenishment',
            page_size: 1000, // consider separate calls for each status if an admin needs to view all trucks
            to_location_ids: truckName
        })
            .then((orders: TransferOrderResult[]) => {
                if (!isMounted) return
                if (orders.length) { //check if any order is already open for receiving
                    const orderToResume = orders.find((order) => order.status === 'receiving')
                    if (orderToResume && orderToResume.tracking && orderToResume.tracking.carrier === 'fedex') {
                        setResumeOrder(orderToResume)
                        setResumeOrderId(orderToResume.tracking.number)
                        window.sessionStorage.setItem('receiving', orderToResume.tracking.number)
                    } else if (orderToResume) {
                        setResumeOrder(orderToResume)
                        setResumeOrderId(orderToResume.transferRequestId.toString())
                        window.sessionStorage.setItem('receiving', orderToResume.transferRequestId.toString())
                    }
                    setTransferOrders(orders)
                }
            })
            .catch((error) => {
                if (isMounted) {
                    dispatch(createError({
                        type: error.type,
                        message: error.message
                    }))
                }
            }).finally(() => {
            setLoading(false)
        })

        return () => {
            isMounted = false
        }
    }, [location.state, receiveSubmitted, dispatch])

    const normalizeTrackingNumber = (n?: string): string => {
        if (!n)
            return ''

        /* remove any non-digits: common to see spaces '7632 4352 3745' */
        n = n.replace(/\D/g, '')

        /* FedEx 34-digit barcodes sometimes slip through, convert to tracking number */
        if (n.length == 34)
            n = n.slice(-14) // grab the last 14 digits

        return n.replace(/^0*/g, '') // trim leading zeros
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault()
            if (selectedId !== null) {
                const sanitizedId = normalizeTrackingNumber(selectedId)
                handleReceive(sanitizedId)
            }
        }
    }

    const performReceive = (transferOrderId: number) => {
        openReceipt(transferOrderId)
            .then((order: OpenReceiptResult) => {
                if (order) {
                    window.sessionStorage.setItem('receiving', transferOrderId.toString())
                    navigate(`/inventory/receiving`, {
                        state: {
                            transferOrder: order
                        }
                    })
                }
            })
            .catch((error) => {
                webEvents.error(
                    error.message,
                    {receiving: transferOrderId},
                    error.stack
                )
                const causeMessage = error?.cause
                    ? error.cause.messages[0]
                    : error.messages[0]

                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
            })
    }

    const handleReceive = (id: string, type?: string) => {
        let currentId = parseInt(id, 10)

        if (currentId !== 0 && !isNaN(currentId) && !currentId) {
            return
        }

        if (type === 'h2h') { //hand-to-hand
            performReceive(currentId)
        } else { //fedex
            //searchTransfer with that tracking number get the transferOrderId
            searchTransfers({tracking_numbers: currentId})
                .then((orders: TransferOrderResult[]) => {
                    if (orders.length) {
                        const transferOrderId = orders[0].transferOrderId
                        performReceive(transferOrderId)
                    } else {
                        dispatch(createError({
                            type: 'Tracking Number Not Found',
                            message: `The tracking number for this package cannot be found. Please contact IT to resolve.`
                        }))
                    }
                })

            //once transfer id exists from ^ open receipt call
            //on success pass the result using state and navigate to recieve page
        }
    }

    const handleResume = () => {
        navigate(`/inventory/receiving`, {
            state: {
                transferOrder: resumeOrder
            }
        })
    }

    const getThirtyDaysAgo = (): Date => {
        const today = new Date()
        today.setDate(today.getDate() - 30)
        return today
    }

    const packages = transferOrders.filter((order) => order.tracking && order.tracking.carrier === 'fedex')
    const readyToReceiveOrders = packages.filter((order) => order.status === 'pending-receipt')
    const pendingOrders = packages.filter((order) => order.status === 'pending-pickup' || order.status === 'in-transit' || order.status === 'out-for-delivery' || order.status === 'error')
    const h3psOrders = transferOrders.filter((order) => order.tracking && order.tracking.carrier === 'h3ps' && order.completed_at === null && order.status !== 'cancelled')
    const recentlyReceivedOrders = packages.filter((order) => {
        const threeDaysAgo = getThirtyDaysAgo()
        return order.status === 'received' && order.completed_at && new Date(order.completed_at) >= threeDaysAgo
    })
    const recentlyReceivedH3psOrders = transferOrders.filter((order) => {
        const threeDaysAgo = getThirtyDaysAgo()
        return order.status === 'received' && order.completed_at && new Date(order.completed_at) >= threeDaysAgo && order.tracking && order.tracking.carrier === 'h3ps'
    })

    const handleSearch = () => {
        const matchedItemIds: string[] = []
        transferOrders.forEach(order => {
            // Search through regular items
            order.items.forEach(item => {
                const matchesSku = item.sku === searchTerm
                const matchesSerial = item.serials?.some(serial =>
                    serial === searchTerm
                )

                if (matchesSku || matchesSerial) {
                    matchedItemIds.push(order.transferOrderId.toString())
                }
            })

            // Search through received items (for recently received orders)
            order.receivedItems?.forEach(receivedItem => {
                const matchesSku = receivedItem.sku === searchTerm
                const matchesSerial = receivedItem.serials?.some(serial =>
                    serial === searchTerm
                )

                if (matchesSku || matchesSerial) {
                    matchedItemIds.push(order.transferOrderId.toString())
                }
            })
        })
        setHighlightedItems(matchedItemIds)
    }

    const handleClear = () => {
        setSearchTerm('')
        setHighlightedItems([])
    }


    return (
        <>
            {receiveSubmitted && (
                <Alert
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '80%',
                        mx: 'auto',
                        alignItems: 'flex-start',
                        my: 1
                    }}
                    icon={<CheckIcon fontSize="inherit"/>}
                    severity="success"
                    onClose={() => setReceiveSubmitted(false)}
                >
                    Package successfully received!
                </Alert>
            )}

            <Box mt={5} display="flex" flexDirection="column" justifyContent="center">
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant="h4" component="h2" gutterBottom
                                sx={{flexGrow: 1, textAlign: 'center', mb: 1}}>
                        Receive
                    </Typography>
                </Box>


                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    width: '90%',
                    px: 2
                }}>
                    <TextField
                        label="Search Item or Serial"
                        variant="outlined"
                        fullWidth
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch()
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchTerm && (
                                        <IconButton
                                            onClick={handleClear}
                                            edge="end"
                                        >
                                            <ClearIcon/>
                                        </IconButton>
                                    )}
                                    <IconButton
                                        onClick={handleSearch}
                                        edge="end"
                                    >
                                        <SearchIcon/>
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            maxWidth: 300,
                            '& .MuiOutlinedInput-root': {
                                paddingRight: 2
                            }
                        }}
                    />
                </Box>

                {isAuthorizedToReceive && <Box display="flex" flexDirection="row" justifyContent="center" m={2}>
                    {resumeOrder ? (
                        <Box display="flex" flexDirection="column">
                            <Button
                                variant="contained"
                                sx={{alignItems: 'center', marginLeft: 1, marginY: 'auto', height: '100%'}}
                                onClick={() => handleResume()}>
                                Resume Receiving {resumeOrderId}
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <TextField
                                label="Scan Tracking Number"
                                variant="outlined"
                                fullWidth
                                value={selectedId}
                                onChange={(e) => setSelectedId(e.target.value.trim())}
                                onKeyDown={handleKeyDown}
                                placeholder="Scan or manually enter a tracking number"
                                inputRef={inputRef}
                                sx={{maxWidth: 500, marginY: 'auto'}}
                            />
                            <Button
                                variant="contained"
                                disabled={!selectedId}
                                sx={{alignItems: 'center', marginLeft: 1, marginY: 'auto', height: '100%'}}
                                onClick={() => handleReceive(selectedId)}>
                                Begin Receiving
                            </Button>
                        </>
                    )}
                </Box>}
            </Box>

            <Box mx="auto" mt={2} display="flex" flexDirection="row" justifyContent="center" alignItems="flex-start">
                <Box mx={3} display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center">
                    <Box display="flex" flexDirection="column" justifyContent="center">
                        <Typography
                            variant="h4"
                            marginY={1}
                            align="center"
                        >
                            Packages
                        </Typography>
                        <Box marginBottom={3}>
                            <Typography
                                variant="h2"
                                component="h2"
                                marginY={1}
                                fontWeight="bold"
                                style={{fontSize: '1.5rem'}}
                            >
                                Ready to Receive
                            </Typography>
                            <ReceiveDashboardTable
                                transferOrders={readyToReceiveOrders}
                                getModelNumber={getModelNumber}
                                highlightedItems={highlightedItems}
                                resumeOrder={resumeOrder}
                            />
                        </Box>
                        <Box marginBottom={3}>
                            <Typography
                                variant="h2"
                                component="h2"
                                fontWeight="bold"
                                marginY={1}
                                style={{fontSize: '1.5rem'}}
                            >
                                On the Way
                            </Typography>
                            <ReceiveDashboardTable
                                transferOrders={pendingOrders}
                                getModelNumber={getModelNumber}
                                highlightedItems={highlightedItems}
                                resumeOrder={resumeOrder}
                            />
                        </Box>
                        <Box marginBottom={3}>
                            <Typography
                                variant="h2"
                                component="h2"
                                marginY={1}
                                fontWeight="bold"
                                style={{fontSize: '1.5rem'}}
                            >
                                Recently Received
                            </Typography>
                            <ReceiveDashboardTable
                                transferOrders={recentlyReceivedOrders}
                                getModelNumber={getModelNumber}
                                received={true}
                                highlightedItems={highlightedItems}
                                resumeOrder={resumeOrder}
                            />
                        </Box>
                    </Box>
                </Box>
                {/*<Divider orientation="vertical" flexItem/>*/}
                {isAuthorizedToReceive && <Box mx={3} display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center">
                    <Box display="flex" flexDirection="column" justifyContent="center">
                        <Typography
                            variant="h4"
                            marginY={1}
                            align="center"
                        >
                            Hand-to-Hand Transfers
                        </Typography>
                        <Box marginBottom={3}>
                            <Typography
                                variant="h2"
                                component="h2"
                                marginY={1}
                                fontWeight="bold"
                                style={{fontSize: '1.5rem'}}
                            >
                                Ready to Receive
                            </Typography>
                            <ReceiveDashboardTable
                                transferOrders={h3psOrders}
                                getModelNumber={getModelNumber}
                                onReceive={handleReceive}
                                showActions={true}
                                handToHand={true}
                                onSelectId={setSelectedId}
                                highlightedItems={highlightedItems}
                                resumeOrder={resumeOrder}
                            />
                        </Box>
                        <Box marginBottom={3}>
                            <Typography
                                variant="h2"
                                component="h2"
                                marginY={1}
                                fontWeight="bold"
                                style={{fontSize: '1.5rem'}}
                            >
                                Recently Received
                            </Typography>
                            <ReceiveDashboardTable
                                transferOrders={recentlyReceivedH3psOrders}
                                getModelNumber={getModelNumber}
                                handToHand={true}
                                received={true}
                                highlightedItems={highlightedItems}
                                resumeOrder={resumeOrder}
                            />
                        </Box>
                    </Box>
                </Box>}
            </Box>
        </>
    )
}

export default ReceiveDashboard
